import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles as SparklesIcon, Tv, Heart, RefreshCw, X } from "lucide-react";
import LangToggle from "../components/LangToggle";
import { fileUrl, getGallery, getKingdomInfo } from "../lib/api";
import { useLang, t } from "../lib/i18n";

const TE = { fontFamily: "'Tiro Telugu','Noto Serif Telugu','Yatra One','Eczar',serif" };
const ROLE_TE = {
  father: "నాన్న",                  mother: "అమ్మ",
  grandfather: "తాతయ్య",             grandmother: "అమ్మమ్మ",
  peddananna: "పెద్దనాన్న",           peddamma: "పెద్దమ్మ",
  chinnanna: "చిన్నాన్న / బాబాయి",   pinni: "పిన్ని",
  attha: "అత్త",                    mamayya: "మామయ్య",
  friend_male: "కుటుంబ స్నేహితుడు", friend_female: "కుటుంబ స్నేహితురాలు",
};
const ROLE_EN = {
  father: "Father",           mother: "Mother",
  grandfather: "Grandfather", grandmother: "Grandmother",
  peddananna: "Peddananna",   peddamma: "Peddamma",
  chinnanna: "Chinnanna",     pinni: "Pinni",
  attha: "Attha",             mamayya: "Mamayya",
  friend_male: "Family Friend", friend_female: "Family Friend",
};

function hash(s) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < (s || "").length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}

// Sunflower spiral position around the moon — pixel-accurate.
// Places star centers in an annulus outside the Moon+halo, guaranteeing
// no star overlaps Anagha's portrait or its glowing halo on any viewport.
function starSpot(i, total, W, H, moonR, starR) {
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  // Reserve top ~120px for the "Anagha's Night Sky" title, and bottom
  // ~60px so the star label under each guest doesn't get clipped.
  const topReserve = 120;
  const bottomReserve = 60;
  const usableH = Math.max(H - topReserve - bottomReserve, 200);
  const cx = W / 2;
  const cy = topReserve + usableH / 2;

  // Inner radius = Moon radius + moon glow padding + star radius (so star
  // edge never overlaps Moon halo). Outer radius keeps the star fully on
  // screen given the reserved margins.
  const glowPad = 22;
  const rMin = moonR + glowPad + starR;
  const maxX = Math.min(cx - starR - 12, W - cx - starR - 12);
  const maxY = Math.min(cy - topReserve - starR, H - bottomReserve - cy - starR);
  const rMax = Math.max(Math.min(maxX, maxY) - 4, rMin + 20);

  const tNorm = total <= 1 ? 0.4 : i / (total - 1);
  // sqrt distribution keeps stars from bunching up in the inner ring
  const r = rMin + Math.sqrt(tNorm) * (rMax - rMin);
  const angle = i * goldenAngle;

  return {
    x: cx + Math.cos(angle) * r,
    y: cy + Math.sin(angle) * r,
  };
}

// Read the CSS clamp(...) formula for a given (min, preferred_vmin_pct, max)
function clampVmin(minPx, vminPct, maxPx, W, H) {
  const vmin = Math.min(W, H);
  return Math.min(Math.max(minPx, vminPct * 0.01 * vmin), maxPx);
}

// Distant twinkling stars (decorative)
function BackgroundStars() {
  const stars = useMemo(() => Array.from({ length: 110 }, (_, i) => {
    const h = hash(`bg-${i}`);
    return {
      id: i,
      x: (h % 1000) / 10,
      y: ((h >> 10) % 1000) / 10,
      size: 1 + (h % 3),
      delay: ((h >> 5) % 80) / 10,
      duration: 2.4 + ((h >> 8) % 30) / 10,
    };
  }), []);
  return (
    <div className="absolute inset-0 pointer-events-none">
      {stars.map((s) => (
        <span
          key={s.id}
          className="absolute rounded-full"
          style={{
            left: `${s.x}%`, top: `${s.y}%`,
            width: s.size, height: s.size,
            background: "white",
            boxShadow: `0 0 ${s.size * 3}px white`,
            animation: `twinkle ${s.duration}s ease-in-out ${s.delay}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

export default function Gallery({ embed }) {
  const lang = useLang();
  const isEmbed = embed === true || (typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("embed") === "1");
  const [guests, setGuests] = useState([]);
  const [info, setInfo] = useState({ princess_name: "Anagha", princess_image: null });
  const [selected, setSelected] = useState(null);
  const [viewport, setViewport] = useState({
    w: typeof window !== "undefined" ? window.innerWidth : 1280,
    h: typeof window !== "undefined" ? window.innerHeight : 720,
  });

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const refresh = async () => {
    try {
      const [g, k] = await Promise.all([getGallery(), getKingdomInfo()]);
      setGuests(g);
      setInfo(k);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    refresh();
    const tm = setInterval(refresh, 5000);
    return () => clearInterval(tm);
  }, []);

  const placed = useMemo(() => {
    const { w: W, h: H } = viewport;
    // Moon size = clamp(140, 26vmin, 280) → diameter, so radius / 2.
    const moonR = clampVmin(140, 26, 280, W, H) / 2;
    // Star face size = clamp(70, 8vmin, 110) → outer square, but the visible
    // halo scales up ~1.8× — use that for spacing safety.
    const starR = clampVmin(70, 8, 110, W, H) / 2 * 1.15;
    const total = Math.max(guests.length, 1);
    return guests.map((g, i) => {
      const { x, y } = starSpot(i, total, W, H, moonR, starR);
      const h = hash(g.id || g.name);
      return {
        guest: g, x, y,
        delay: (h % 100) / 30,
        duration: 2.4 + (h % 30) / 10,
      };
    });
  }, [guests, viewport]);

  const princessSrc = info.princess_image ? fileUrl(info.princess_image) : null;

  return (
    <div
      className="fixed inset-0 overflow-hidden text-blush"
      style={{
        background:
          "radial-gradient(ellipse at center, #1d1252 0%, #0c0830 55%, #02021a 100%)",
      }}
      data-testid="night-sky"
    >
      <BackgroundStars />

      {/* Soft purple/blue nebulae */}
      <div className="absolute -top-1/4 -left-1/4 w-[70vw] h-[70vw] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(120,90,220,0.22), transparent 70%)" }} />
      <div className="absolute -bottom-1/4 -right-1/4 w-[70vw] h-[70vw] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(220,90,180,0.18), transparent 70%)" }} />

      {/* Header — hidden when embedded inside Director iframe to avoid double chrome */}
      {!isEmbed && (
        <header className="absolute top-0 left-0 right-0 px-4 sm:px-8 pt-4 sm:pt-5 flex items-center justify-between flex-wrap gap-2 z-30" style={TE}>
        <Link to="/welcome" className="flex items-center gap-2 label-eyebrow text-blush/85 hover:text-blush">
          <Heart size={14} className="text-[#FFC4D6]" fill="#FFC4D6" /> {t("back", lang)}
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/finale" className="btn-royal text-xs py-2 px-3" data-testid="gallery-finale-link">
            <SparklesIcon size={12} />
            <span className="hidden sm:inline">{t("gal_finale", lang)}</span>
          </Link>
          <Link to="/director" className="btn-ghost-royal text-xs py-2 px-3 text-blush border-blush/40 hover:bg-white/10" data-testid="gallery-projector-link">
            <Tv size={12} />
            <span className="hidden sm:inline">{t("projector", lang)}</span>
          </Link>
          <button onClick={refresh} className="glass rounded-full p-2 text-plum" title="Refresh">
            <RefreshCw size={14} />
          </button>
          <LangToggle light />
        </div>
      </header>
      )}

      {/* Title */}
      <div className="absolute top-16 sm:top-20 left-0 right-0 text-center px-4 z-20 pointer-events-none" style={TE}>
        <p className="font-script gold-text drop-shadow-2xl" style={{ fontSize: "clamp(22px, 4.6vw, 40px)" }}>
          {lang === "te" ? "అనఘ ఆకాశం" : "Anagha's Night Sky"}
        </p>
        <p className="font-display text-blush/85" data-testid="gallery-star-count" style={{ fontSize: "clamp(11px, 2.4vw, 14px)" }}>
          {placed.length} {lang === "te" ? "నక్షత్రాలు మెరుస్తున్నాయి · ఏదైనా నక్షత్రాన్ని తాకండి" : "stars are shining · tap any star"}
        </p>
      </div>

      {/* Moon — Anagha at the centre */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
        <div
          className="rounded-full overflow-hidden animate-[moonGlow_5s_ease-in-out_infinite]"
          style={{
            width: "clamp(140px, 26vmin, 280px)",
            height: "clamp(140px, 26vmin, 280px)",
            border: "4px solid #FCF6BA",
            boxShadow:
              "0 0 60px 18px rgba(252,246,186,0.55), 0 0 140px 50px rgba(255,210,150,0.35), inset 0 0 30px rgba(255,255,255,0.3)",
          }}
        >
          {princessSrc ? (
            <img src={princessSrc} alt="Anagha" className="w-full h-full object-cover" crossOrigin="anonymous" />
          ) : (
            <div className="w-full h-full grid place-items-center"
              style={{ background: "radial-gradient(circle at 35% 30%, #fff7d8, #f3d77a 60%, #b8852c)" }}>
              <span className="text-plum-deep font-display text-3xl">A</span>
            </div>
          )}
        </div>
        <div className="text-center mt-2">
          <p className="font-script text-blush drop-shadow" style={{ ...TE, fontSize: "clamp(18px, 3.4vw, 28px)" }}>
            {lang === "te" ? "యువరాణి అనఘ" : info.princess_name || "Princess Anagha"}
          </p>
        </div>
      </div>

      {/* Guest stars — gold 4-point star with the guest's photo set inside */}
      {placed.map((p) => (
        <button
          key={p.guest.id}
          onClick={() => setSelected(p.guest)}
          className="absolute -translate-x-1/2 -translate-y-1/2 z-20 group focus:outline-none"
          style={{
            left: `${p.x}px`,
            top: `${p.y}px`,
            animation: `starPulse ${p.duration}s ease-in-out ${p.delay}s infinite`,
          }}
          data-testid={`star-${p.guest.id}`}
          title={p.guest.name}
        >
          {/* Star = SVG points + photo circle inside. Face is always at least 40px so it stays visible. */}
          <div
            className="relative"
            style={{
              width: "clamp(70px, 8vmin, 110px)",
              height: "clamp(70px, 8vmin, 110px)",
            }}
          >
            {/* Outer glow halo */}
            <span
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{
                background: "radial-gradient(circle, rgba(252,246,186,0.55), transparent 70%)",
                transform: "scale(1.8)",
              }}
            />
            {/* Gold 4-point star burst behind the face */}
            <svg viewBox="-50 -50 100 100" className="absolute inset-0 w-full h-full overflow-visible">
              <defs>
                <radialGradient id={`g-${p.guest.id}`} cx="50%" cy="40%" r="60%">
                  <stop offset="0%"  stopColor="#FCF6BA" />
                  <stop offset="60%" stopColor="#D4AF37" />
                  <stop offset="100%" stopColor="#8B6A1A" />
                </radialGradient>
              </defs>
              <path
                d="M 0 -50 L 10 -10 L 50 0 L 10 10 L 0 50 L -10 10 L -50 0 L -10 -10 Z"
                fill={`url(#g-${p.guest.id})`}
                stroke="#fff7d8"
                strokeWidth="1.2"
                opacity="0.95"
              />
            </svg>
            {/* Face circle — always visible thanks to clamp() minimum */}
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full overflow-hidden ring-2 ring-[#FCF6BA] shadow-lg"
              style={{
                width: "55%",
                height: "55%",
                background: "#fff",
                boxShadow: "0 0 14px rgba(252,246,186,0.85), 0 0 0 1px rgba(255,255,255,0.4)",
              }}
            >
              <img
                src={fileUrl(p.guest.character_path || p.guest.selfie_path)}
                alt={p.guest.name}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <span
            className="absolute left-1/2 -translate-x-1/2 mt-1 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity font-script text-blush drop-shadow"
            style={{ ...TE, fontSize: "clamp(10px, 1.7vw, 13px)", top: "100%" }}
          >
            {p.guest.name}
          </span>
        </button>
      ))}

      {/* Empty state */}
      {!guests.length && (
        <div className="absolute left-1/2 bottom-12 -translate-x-1/2 text-center text-blush/80 px-6" style={TE}>
          <p className="font-script" style={{ fontSize: "clamp(18px, 4vw, 28px)" }}>
            {lang === "te" ? "ఇంకా నక్షత్రాలు మెరవలేదు" : "No stars are shining yet"}
          </p>
          <Link to="/register" className="btn-royal mt-3 inline-flex" data-testid="gallery-first-btn">
            {lang === "te" ? "మొదటి నక్షత్రం కావండి" : "Be the first star"}
          </Link>
        </div>
      )}

      {/* Star detail modal */}
      {selected && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
          onKeyDown={(e) => { if (e.key === "Escape") setSelected(null); }}
          data-testid="star-modal"
        >
          <div
            className="relative w-full max-w-md rounded-3xl overflow-hidden text-left"
            onClick={(e) => e.stopPropagation()}
            style={{
              background:
                "linear-gradient(180deg, rgba(40,15,75,0.92), rgba(15,8,45,0.95))",
              border: "2px solid #D4AF37",
              boxShadow: "0 30px 80px rgba(0,0,0,0.7), 0 0 60px rgba(252,246,186,0.25)",
            }}
          >
            <button
              className="absolute top-3 right-3 z-10 rounded-full p-2 bg-white/15 hover:bg-white/30 text-blush"
              onClick={() => setSelected(null)}
              data-testid="star-modal-close"
            >
              <X size={16} />
            </button>

            <div className="relative h-56 sm:h-72 overflow-hidden">
              <img
                src={fileUrl(selected.character_path || selected.selfie_path)}
                alt={selected.name}
                className="w-full h-full object-cover"
                crossOrigin="anonymous"
              />
              <div className="absolute inset-x-0 bottom-0 h-2/3"
                style={{ background: "linear-gradient(180deg, transparent, rgba(15,8,45,0.95))" }} />
              <div className="absolute bottom-3 left-4 right-4">
                <p className="font-script gold-text" style={{ ...TE, fontSize: "clamp(22px, 4.6vw, 32px)" }}>
                  {selected.name}
                </p>
                <p className="label-eyebrow text-blush/85" style={TE}>
                  {(lang === "te" ? ROLE_TE : ROLE_EN)[selected.relationship] || selected.relationship}
                </p>
              </div>
            </div>

            <div className="p-5 sm:p-6">
              <p className="label-eyebrow text-blush/70 mb-2" style={TE}>
                <Heart size={11} className="inline -mt-1 mr-1 text-[#FFC4D6]" fill="#FFC4D6" />
                {lang === "te" ? "అనఘ నుండి ఆశీర్వాదం" : "Blessing from Anagha"}
              </p>
              <p
                className="font-display italic text-blush leading-relaxed"
                style={{ ...TE, fontSize: "clamp(14px, 3vw, 18px)" }}
              >
                {selected.personal_message
                  ? `“${selected.personal_message}”`
                  : (lang === "te"
                      ? "ఈ నక్షత్రానికి ఇంకా ఆశీర్వాదం రాయబడలేదు."
                      : "No blessing has been written for this star yet.")}
              </p>
              <p className="font-script text-blush/90 text-right mt-3" style={{ ...TE, fontSize: "clamp(16px, 3.2vw, 22px)" }}>
                — {lang === "te" ? "యువరాణి అనఘ ✦" : (info.princess_name || "Princess Anagha") + " ✦"}
              </p>
              {selected.loving_title && (
                <p className="label-eyebrow text-[#FCF6BA]/90 mt-4 text-center" style={TE}>
                  ✧ {selected.loving_title} ✧
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes twinkle {
          0%,100% { opacity: 0.3; transform: scale(1); }
          50%     { opacity: 1;   transform: scale(1.4); }
        }
        @keyframes starPulse {
          0%,100% { transform: translate(-50%, -50%) scale(1) rotate(0deg); }
          50%     { transform: translate(-50%, -50%) scale(1.25) rotate(8deg); }
        }
        @keyframes moonGlow {
          0%,100% { box-shadow:
              0 0 60px 18px rgba(252,246,186,0.55),
              0 0 140px 50px rgba(255,210,150,0.30),
              inset 0 0 30px rgba(255,255,255,0.3); }
          50%     { box-shadow:
              0 0 80px 28px rgba(252,246,186,0.75),
              0 0 200px 70px rgba(255,210,150,0.40),
              inset 0 0 36px rgba(255,255,255,0.4); }
        }
      `}</style>
    </div>
  );
}
